import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { DatePicker } from "antd-mobile";

import type {
  GetMoneyDetailData,
  GetStaffCapitalListsQuery,
  CapitalItem as CapitalItemType,
} from "../../types";
import { CapitalItem } from "./components/CapitalItem";
import { getCapitalList, getIncome } from "@/api/staff";

const BRAND = "#FF6B35";
const BRAND_GRADIENT = "linear-gradient(135deg, #FF6B35, #FF9A5C)";

const TABS = [
  { title: "全部", id: -1 },
  { title: "收入", id: 1 },
  { title: "提现", id: 0 },
];

/** 通用列表分页 Hook */
function useListPage<T>(
  fetchFn: (query: Record<string, unknown>) => Promise<T[]>,
  baseQuery: Record<string, unknown>,
) {
  const [data, setData] = useState<T[]>([]);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [loadingStatus, setLoadingStatus] = useState<"more" | "no-more" | "loading">("more");

  const init = async (reset = false) => {
    if (loading) return;
    const currentPage = reset ? 1 : page;
    setLoading(true);
    setLoadingStatus("loading");
    try {
      const list = (await fetchFn({ ...baseQuery, page: currentPage })) ?? [];
      setData((prev) => (reset ? list : [...prev, ...list]));
      if ((list.length ?? 0) < Number(baseQuery.limit || 10)) {
        setLoadingStatus("no-more");
      } else {
        setLoadingStatus("more");
        if (reset) setPage(1);
      }
    } catch (err) {
      console.error("[ListPage Error]", err);
      setLoadingStatus("no-more");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (page > 1) init(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  return { data, loading, loadingStatus, init, page, setPage, setData, setLoadingStatus };
}

export default function CapitalDetailPage() {
  const navigate = useNavigate();
  const [moneyData, setMoneyData] = useState<GetMoneyDetailData | null>(null);
  const [activeTab, setActiveTab] = useState(-1);
  const [startTime, setStartTime] = useState("");
  const [endTime, setEndTime] = useState("");
  const [showStartPicker, setShowStartPicker] = useState(false);
  const [showEndPicker, setShowEndPicker] = useState(false);
  const formRef = useRef<GetStaffCapitalListsQuery>({ pm: "", limit: 10 });

  const fetchCapitalLists = async (query: Record<string, unknown>): Promise<CapitalItemType[]> => {
    const pm = query.pm === "" ? "" : String(query.pm ?? "");
    const page = Number(query.page ?? 1);
    const limit = Number(query.limit ?? 10);
    const start_time = typeof query.start_time === "string" ? query.start_time : undefined;
    const end_time = typeof query.end_time === "string" ? query.end_time : undefined;
    const res = await getCapitalList({
      page,
      limit,
      pm,
      start_time,
      end_time,
    });
    if (Array.isArray(res)) return res as CapitalItemType[];
    const anyRes = res as any;
    if (Array.isArray(anyRes?.list)) return anyRes.list as CapitalItemType[];
    if (Array.isArray(anyRes?.data)) return anyRes.data as CapitalItemType[];
    return [];
  };

  const {
    data,
    loading,
    loadingStatus,
    init,
    page,
    setPage,
    setData,
    setLoadingStatus,
  } = useListPage<CapitalItemType>(fetchCapitalLists, formRef.current);

  const loadMoreRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        // uni-app 使用 getMoneyDetail；Web 侧用 getIncome 复用 remain_price/freeze_price 字段
        const income = await getIncome();
        if (!alive) return;
        setMoneyData({
          remain_price: income.remain_price ?? "0.00",
          freeze_price: income.freeze_price ?? 0,
        });
      } catch {
        // 错误提示在 request 层统一处理
      }
    })();

    init(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const el = loadMoreRef.current;
    if (!el) return;
    const io = new IntersectionObserver(
      (entries) => {
        const e = entries[0];
        if (!e?.isIntersecting) return;
        if (loading) return;
        if (loadingStatus !== "more") return;
        setPage((p) => p + 1);
      },
      { root: null, rootMargin: "200px 0px 200px 0px", threshold: 0 },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [loading, loadingStatus, setPage]);

  const handleSearch = () => {
    formRef.current.start_time = startTime;
    formRef.current.end_time = endTime;
    setLoadingStatus("more");
    setData([]);
    init(true);
  };

  const handleClear = () => {
    setStartTime("");
    setEndTime("");
    formRef.current.start_time = "";
    formRef.current.end_time = "";
    setLoadingStatus("more");
    setData([]);
    init(true);
  };

  return (
    <div className="min-h-screen pb-[calc(env(safe-area-inset-bottom,0px)+80px)]" style={{ background: "#F7F7F7", fontFamily: "'SF Pro Text','PingFang SC','Inter',system-ui,sans-serif" }}>
      {/* Header — 白色导航栏 */}
      <div className="bg-white px-5 flex items-center gap-3" style={{ paddingTop: "calc(env(safe-area-inset-top,0px) + 16px)", paddingBottom: 13, borderBottom: "1px solid #F5F5F5" }}>
        <button onClick={() => navigate(-1)} className="w-9 h-9 flex items-center justify-center rounded-full cursor-pointer active:scale-[0.98] transition-transform shrink-0" style={{ background: "#F3F4F6" }}>
          <i className="ri-arrow-left-line" style={{ fontSize: 20, color: "#374151" }} />
        </button>
        <span className="text-lg font-bold text-gray-800">资金明细</span>
      </div>

      {/* 资金卡片 */}
      <div className="px-4 pt-4 space-y-3">
        <div className="bg-white rounded-2xl overflow-hidden p-4">
          {/* 可提现金额行 */}
          <div className="flex items-center justify-between">
            <div>
              <span className="text-xs text-gray-500">可提现金额</span>
              <div className="text-3xl font-bold mt-1" style={{ color: "#EF4444" }}>
                ¥{moneyData?.remain_price ?? "0.00"}
              </div>
            </div>
            <button
              onClick={() => navigate(`/workspace/money/withdraw?money=${encodeURIComponent(String(moneyData?.remain_price ?? 0))}`)}
              className="px-5 py-2 rounded-full text-sm font-semibold text-white active:scale-[0.98] transition-all"
              style={{ background: BRAND }}
            >
              去提现
            </button>
          </div>
          {/* 分割线 + 未结算金额 */}
          <div className="mt-4 pt-4 border-t border-gray-100 flex items-center justify-between">
            <span className="text-sm text-gray-500">未结算金额</span>
            <span className="text-base font-bold text-gray-900">¥{moneyData?.freeze_price ?? "0.00"}</span>
          </div>
        </div>

        {/* 筛选栏 */}
        <div className="bg-white rounded-2xl px-4 py-3 flex flex-col gap-3">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-2 flex-1 min-w-0">
              <button
                type="button"
                onClick={() => setShowStartPicker(true)}
                className="flex-1 min-w-0 py-2 px-3 rounded-xl bg-gray-50 text-sm text-left text-gray-700 truncate"
              >
                {startTime || "开始时间"}
                <i className="ri-calendar-2-line ml-2 text-gray-400" />
              </button>
              <button
                type="button"
                onClick={() => setShowEndPicker(true)}
                className="flex-1 min-w-0 py-2 px-3 rounded-xl bg-gray-50 text-sm text-left text-gray-700 truncate"
              >
                {endTime || "结束时间"}
                <i className="ri-calendar-2-line ml-2 text-gray-400" />
              </button>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <button onClick={handleSearch} className="px-4 py-2 rounded-full text-xs font-semibold text-white active:scale-[0.98] transition-all" style={{ background: BRAND }}>搜索</button>
              <button onClick={handleClear} className="px-4 py-2 rounded-full text-xs font-semibold text-gray-600 bg-gray-100 active:scale-[0.98] transition-all">清除</button>
            </div>
          </div>

          {(startTime || endTime) && (
            <div className="text-xs text-gray-400">
              当前筛选：{startTime || "—"} ~ {endTime || "—"}
            </div>
          )}
        </div>

        {/* Tab 栏 */}
        <div className="bg-white rounded-2xl px-4 flex gap-6">
          {TABS.map((tab) => (
            <button
              key={tab.id}
              onClick={() => {
                setActiveTab(tab.id);
                formRef.current.pm = tab.id === -1 ? "" : tab.id;
                setLoadingStatus("more");
                setData([]);
                init(true);
              }}
              className={`py-3.5 text-sm relative ${
                activeTab === tab.id ? "font-semibold" : "text-gray-500"
              }`}
            >
              {tab.title}
              {activeTab === tab.id && (
                <span className="absolute bottom-0 left-1/2 -translate-x-1/2 w-5 h-[3px] rounded-full" style={{ background: BRAND }} />
              )}
            </button>
          ))}
        </div>

        {/* 列表 */}
        <div className="space-y-3 pb-4">
          {data.map((item) => (
            <CapitalItem key={item.id} item={item} />
          ))}
          {!loading && !data.length && (
            <div className="pt-16 pb-8 text-center">
              <i className="ri-file-list-3-line text-5xl block mx-auto mb-3 text-gray-200" />
              <p className="text-sm text-gray-400">暂无数据</p>
            </div>
          )}
          {loading && (
            <div className="pt-4 text-center text-gray-400 text-xs">加载中...</div>
          )}
          {loadingStatus === "no-more" && data.length > 0 && (
            <div className="pb-4 text-center text-gray-300 text-xs">— 已经到底了 —</div>
          )}
          {/* 触底加载哨兵 */}
          <div ref={loadMoreRef} style={{ height: 1 }} />
        </div>
      </div>

      {/* 日期选择器（底部弹出） */}
      <DatePicker
        visible={showStartPicker}
        onClose={() => setShowStartPicker(false)}
        max={endTime ? new Date(endTime) : new Date()}
        onConfirm={(d) => {
          const nextStart = d.toISOString().slice(0, 10);
          setStartTime(nextStart);
          // 保证结束时间不早于开始时间
          if (endTime && endTime < nextStart) setEndTime(nextStart);
          setShowStartPicker(false);
        }}
      />
      <DatePicker
        visible={showEndPicker}
        onClose={() => setShowEndPicker(false)}
        min={startTime ? new Date(startTime) : undefined}
        max={new Date()}
        onConfirm={(d) => {
          const nextEnd = d.toISOString().slice(0, 10);
          // 兜底：即使 min 生效，也保证结束时间不早于开始时间
          if (startTime && nextEnd < startTime) setEndTime(startTime);
          else setEndTime(nextEnd);
          setShowEndPicker(false);
        }}
      />
    </div>
  );
}
